import {
  type HistoryObjectType,
  HistoryStoreClassKeys,
  type HistoryStoreFilterRequest,
  HistoryStoreProtextedKeys,
  type HistoryStoreSystemAtts,
  IHistoryObjectFunctions,
  type IndexRange,
  type MapLike,
  type MetricStatsType,
  NumNullArr,
  SortOrder,
  type StateSnapshot,
  TypedHistoryStore,
  TypedStateSnapshot,
} from '@iotv/iotv-v3-types'
import { getNiceDate, timestampToMS } from '../../util/time/millisecondOperations'
import { enumerable } from '../constructs/enumerableDecorator'
import { CMVarAggregationMap } from './CMVarAggregation'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../util/lambda'
import { isNumber } from '../../util/number'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

const debugDateFormat: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }

/**
 * Class Time series data storage object with methods for manipulation
 
 * Note undefined values held in arrays must be null not undefined to corretly marshall / unmarshall in document client
*/

export class HistoryStore<T extends { [k: string]: any } = { [k: string]: any }> implements IHistoryObjectFunctions, HistoryObjectType {
  /**
   * @param zombie
   */
  constructor(zombie?: Partial<HistoryObjectType>) {
    /** index is to be deprecated as is superfluous */
    this.index = []
    this.timestamp = []

    if (zombie) {
      const { statistics, ...others } = zombie
      Object.assign(this, others)
      this.statistics = new CMVarAggregationMap(statistics)
    }
  }

  static append = (historyStoresU: (HistoryStore | undefined)[]) => {
    const historyStores = historyStoresU.filter((x) => x !== undefined) as HistoryStore[]
    historyStores.sort((s1, s2) => s1.getLastTimestamp() - s2.getLastTimestamp())

    const slicedZombies = historyStores.map((store, i) => {
      const laterStore: HistoryStore | undefined = historyStores[i + 1]
      const cutoffTimestamp: number | undefined = laterStore?.getFirstTimestamp()
      const storeLastIdxLessThanTimestamp = Math.max(store.getIndexLastLessThanTimestamp(cutoffTimestamp), 0)
      const slice = cutoffTimestamp ? store.slice([0, storeLastIdxLessThanTimestamp]) : store
      return slice
    })

    const emptyHistory = new HistoryStore()
    const merged = slicedZombies.reduce((acc, curr) => {
      acc.index = acc.index.concat(curr.index)
      acc.timestamp = acc.timestamp.concat(curr.timestamp)
      acc.systemProperties = {
        autoTrim: false,
        maxAgeMS: undefined,
        maxLength: undefined,
      }
      if (acc.statistics) {
        debug && console.log(`Zeroing statitistics on append`)
        acc.statistics = new CMVarAggregationMap({
          data: curr.statistics?.data ?? {},
        })
      }
      Object.entries(curr)
        .filter(([k, v]) => curr.keys().includes(k))
        .forEach(([k, v]) => {
          if (!acc[k]) acc[k] = []
          if (acc[k] instanceof Array) {
            acc[k] = [...acc[k], ...v]
          }
        })
      debug && console.log('acc timestamp', acc.timestamp)
      return acc
    }, emptyHistory)
    return merged
  }

  protectedKeys: HistoryStoreProtextedKeys = ['timestamp', 'index', 'systemProperties', 'protectedKeys', 'statistics']

  index: number[]
  timestamp: number[]
  statistics?: CMVarAggregationMap
  systemProperties?: HistoryStoreSystemAtts;
  [k: string]: any

  @enumerable(false)
  isProtectedKey = (k: string) => (this.protectedKeys as string[]).includes(k)

  @enumerable(false)
  initializeSystemProperties() {
    if (!this.systemProperties)
      this.systemProperties = {
        autoTrim: false,
        maxAgeMS: undefined,
        maxLength: undefined,
      }
  }

  @enumerable(false)
  setAutoTrim(autoTrim: boolean) {
    this.initializeSystemProperties()
    this.systemProperties && (this.systemProperties.autoTrim = autoTrim)
  }

  @enumerable(false)
  setMaxLength(maxLength: number | undefined) {
    this.initializeSystemProperties()
    this.systemProperties && (this.systemProperties.maxLength = maxLength)
  }

  @enumerable(false)
  getMaxLength(): number | undefined {
    return this.systemProperties?.maxLength
  }

  @enumerable(false)
  setMaxAge(maxAgeMS: number | undefined) {
    this.initializeSystemProperties()
    this.systemProperties && (this.systemProperties.maxAgeMS = maxAgeMS)
  }

  @enumerable(false)
  getMaxLAge(): number | undefined {
    return this.systemProperties?.maxAgeMS
  }

  @enumerable(false)
  trimToLength() {
    if (this.systemProperties?.maxLength) {
      let shifts = 0
      while (this.getLength() > this.systemProperties.maxLength) {
        this.shift()
        shifts++
      }
      // debug && console.log(`with systemProperties.maxLength ${this.systemProperties.maxLength} shifted ${shifts} to become length ${this.getLength() }`)
    }
  }

  @enumerable(false)
  trimToAgeMS(maxAge: number) {
    const maxAgeMs = timestampToMS(maxAge)
    const doTrim = () => {
      const oldestTimestamp = timestampToMS(this.timestamp[0])
      return oldestTimestamp && maxAgeMs && oldestTimestamp < maxAgeMs
    }

    let shifts = 0
    while (doTrim()) {
      const shiftedSnapshot = this.shift()
      // debug && console.log('shifted', shiftedSnapshot)
      shifts++
    }
    //debug && console.log(`with maxAge ${ maxAgeMs ? new Date(maxAgeMs) : 'undefined'} shifted ${shifts} to have oldest ${ new Date( this.timestamp[0] )  }, length now ${this.getLength()}`)
  }

  @enumerable(false)
  keys() {
    return Object.keys(this).filter((key) => this[key] instanceof Array && !this.isProtectedKey(key))
  }

  @enumerable(false)
  hasKey(key: string) {
    return this.keys().includes(key)
  }

  @enumerable(false)
  toZombie(): HistoryObjectType {
    let zombie: HistoryObjectType = {} as HistoryObjectType
    Object.assign(zombie, this)
    // Object.keys(this).filter( ( key ) => ![ 'timestamp', 'index' ].includes(key))
    return zombie
  }

  @enumerable(false)
  getLength() {
    return this.timestamp.length
  }

  @enumerable(false)
  getLastIndex() {
    return this.timestamp.length - 1
  }

  @enumerable(false)
  getLastTimestamp() {
    return this.timestamp[this.getLastIndex()]
  }

  @enumerable(false)
  getFirstTimestamp() {
    return this.timestamp[0]
  }

  @enumerable(false)
  autoTrimFn() {
    if (this.systemProperties?.autoTrim) {
      if (this.systemProperties?.maxLength) {
        // debug && console.log('Trimming to length',  this.systemProperties.maxLength)
        this.trimToLength()
      }
      if (this.systemProperties?.maxAgeMS) {
        const newestTs = this.timestamp[this.timestamp.length - 1]
        const newestTsMS = timestampToMS(newestTs)
        if (newestTsMS) {
          //  debug && console.log('Trimming to ', newestTsMS - this.systemProperties.maxAgeMS)
          this.trimToAgeMS(newestTsMS - this.systemProperties.maxAgeMS)
        }
      }
    }
  }

  @enumerable(false)
  filter(historyStoreFilter: HistoryStoreFilterRequest): HistoryStore {
    let slicedStore: HistoryStore | undefined = undefined
    if (historyStoreFilter.lastInNMs) {
      debug && console.log(`Filtering to last ${historyStoreFilter.lastInNMs} ms`)
      const lastMs = this.getLastTimestamp()
      const startMs = lastMs - historyStoreFilter.lastInNMs
      slicedStore = this.sliceByTimestamp([startMs, lastMs])
    } else if (historyStoreFilter.lastN) {
      debug && console.log(`Filtering to last ${historyStoreFilter.lastN} entries`)
      const lastIdx = this.getLastIndex()
      const startIdx = Math.max(0, lastIdx - historyStoreFilter.lastN)
      slicedStore = this.slice([startIdx, lastIdx + 1])
      debug && console.log('Sliced length', slicedStore.getLength())
    } else if (historyStoreFilter.period) {
      const { fromDateMs, toDateMs } = historyStoreFilter.period

      slicedStore = this.sliceByTimestamp([fromDateMs, toDateMs])
      debug && console.log(`Requested between ${getNiceDate(fromDateMs)} to ${getNiceDate(toDateMs)}`)
      debug && console.log('Sliced length', slicedStore.getLength())
      debug &&
        console.log(
          `Sliced from ${getNiceDate(slicedStore.timestamp[0], debugDateFormat)}`,
          `to ${getNiceDate(slicedStore.getLastTimestamp(), debugDateFormat)} `,
        )
      debug &&
        console.log(`Original range from ${getNiceDate(this.timestamp[0], debugDateFormat)}`, `to ${getNiceDate(this.getLastTimestamp(), debugDateFormat)} `)
    }

    if (slicedStore && historyStoreFilter.sampleRate) {
      debug && console.log(`Sparsifying to at sample rate ${historyStoreFilter.sampleRate}`)

      slicedStore = this.diceStore(slicedStore, historyStoreFilter.sampleRate)
    }
    return slicedStore ?? this.clone()
  }

  /**
   * Function to thin data in store by sampling
   */

  @enumerable(false)
  diceStore(historyStore: HistoryStore, sampleRate: number): HistoryStore {
    const { statistics, systemProperties } = historyStore
    const dicedStore = new HistoryStore({ statistics, systemProperties })
    dicedStore.resetStatistics()
    const increment = Math.max(Math.floor(sampleRate), 1)
    const max = historyStore.getLastIndex()
    for (let i = 0; i <= max; i += increment) {
      const stateSnapshot = historyStore.getStateSnapshotByIndex(i)
      if (stateSnapshot) {
        dicedStore.push(stateSnapshot)
      }
      if (i % 1000 === 0) {
        debug && console.log(`length ${historyStore.getLength()} snapshot at ${i}`, stateSnapshot)
      }
    }

    return dicedStore
  }

  @enumerable(false)
  push(snapshot: StateSnapshot<any>, update: boolean = true) {
    debug && console.log('History Store push', snapshot)
    const snapShotKeys: string[] = Object.keys(snapshot.state)
    this.index.push(this.getLength())
    this.timestamp.push(snapshot.timestamp)
    const unsetKeys = this.keys().filter((k: string) => !snapShotKeys.includes(k))
    unsetKeys.forEach((k: string) => this.pushKV(k, null))
    Object.entries(snapshot.state).forEach(([k, v]) => !this.isProtectedKey(k) && this.pushKV(k, v))
    if (update) {
      this.updateStatistics(snapshot)
    }
    this.autoTrimFn()
    return this
  }
  @enumerable(false)
  unshift(snapshot: StateSnapshot<any>) {
    const snapShotKeys: string[] = Object.keys(snapshot.state)
    this.index.push(this.getLength())
    this.timestamp.unshift(snapshot.timestamp)
    const unsetKeys = this.keys().filter((k: string) => !snapShotKeys.includes(k))
    unsetKeys.forEach((k: string) => this.unshiftKV(k, null))
    Object.entries(snapshot.state).forEach(([k, v]) => !this.isProtectedKey(k) && this.unshiftKV(k, v))
    this.updateStatistics(snapshot)
    this.autoTrimFn()
    return this
  }

  @enumerable(false)
  pop() {
    let stateSnapshot: StateSnapshot<any> | undefined = undefined
    if (this.getLength() > 0) {
      this.index.pop()
      stateSnapshot = { timestamp: 0, state: {} }
      stateSnapshot.timestamp = this.timestamp.pop() as number
      this.keys().forEach((k) => {
        if (stateSnapshot) stateSnapshot.state[k] = this[k].pop()
      })
    }
    return stateSnapshot
  }

  @enumerable(false)
  shift() {
    let stateSnapshot: StateSnapshot<any> | undefined = undefined
    if (this.getLength() > 0) {
      this.index.pop()
      stateSnapshot = { timestamp: 0, state: {} }
      stateSnapshot.timestamp = this.timestamp.shift() as number
      this.keys().forEach((k) => {
        if (stateSnapshot && typeof this[k].shift === 'function') {
          stateSnapshot.state[k] = this[k].shift()
        } else {
          debug && console.log('Probably not an array', k)
          debug && console.log('this.keys ', this.keys())
        }
      })
    }
    return stateSnapshot
  }
  /** returns a copy of the store sliced */
  @enumerable(false)
  slice(range: IndexRange, opt: string = '') {
    debug && console.log('Sliceing by index', opt, range)
    let historySlice = new HistoryStore()
    historySlice.index = this.index.slice(0, range[1] - range[0])
    historySlice.timestamp = this.timestamp.slice(range[0], range[1])
    this.keys().forEach((k) => {
      historySlice[k] = this[k].slice(range[0], range[1])
    })
    return historySlice
  }

  @enumerable(false)
  peek(idx: number, k: string) {
    return this[k]?.[idx] ?? null
  }

  @enumerable(false)
  peekRange([start, end]: [number, number], k: string) {
    return this[k]?.slice(start, end) ?? null
  }

  peekLastN(n: number, k: string) {
    const start = Math.max(this.getLastIndex() - n, 0)
    return this[k]?.slice(start) ?? null
  }

  @enumerable(false)
  peekLast(k: string) {
    const idx = this.getLastIndex()
    return this[k]?.[idx] ?? null
  }

  @enumerable(false)
  clone() {
    const clone = this.slice([0, this.getLength()])
    return clone
  }

  @enumerable(false)
  mergeLast(snapshot: StateSnapshot<any>) {
    const lastState = this.pop()
    if (lastState?.state) {
      Object.entries(snapshot.state).forEach(([k, v]) => {
        if (v !== null && v) {
          lastState.state[k] = v
        }
      })
      this.push(lastState)
    }
  }

  @enumerable(false)
  updateStatistics(snapshot: StateSnapshot<any>) {
    if (this.statistics instanceof CMVarAggregationMap) {
      this.statistics.update(snapshot)
    }
  }

  @enumerable(false)
  resetStatistics(snapshot?: StateSnapshot<any>) {
    this.statistics = new CMVarAggregationMap()
    if (snapshot) {
      this.statistics.update(snapshot)
    }
  }

  @enumerable(false)
  getStatistics() {
    const stats: MapLike<MetricStatsType> = {}
    if (this.statistics?.data) {
      const stData = this.statistics.data
      this.keys().forEach((k: string) => {
        const st = stData[k]
        if (st) {
          stats[k] = {
            firstTimestamp: st.firstTimestamp,
            lastTimeStamp: st.lastTimeStamp,
            count: st.cn,
            mean: st.mean,
            sdPop: st.sdPop,
            sdSample: st.sdSample,
            varPop: st.variancePop,
            varSample: st.varianceSample,
            max: st.max,
            min: st.min,
          }
        }
      })
    }
    return stats
  }

  @enumerable(false)
  private getTimestampRange(timestampRange: [number, number]): IndexRange {
    const first = Math.max(this.getIndexLastLessThanTimestamp(timestampRange[0]), 0) // Index less than timestamp can be -1 if provided time is before first timestamp
    const latest = this.getIndexLastLessThanTimestamp(timestampRange[1] + 1) // Because it's strictly less than, we gotta add one to get timestamp that are right on the last stamp

    debug && console.log(`Index less than ${new Date(timestampRange[0])}: `, first)
    debug && console.log(`Index less than ${new Date(timestampRange[1])}: `, latest)

    return [first, latest]
  }

  @enumerable(false)
  sliceByTimestamp(timestampRange: [number, number]): HistoryStore {
    debug && console.log('Time stamp range to slice by', timestampRange)
    const timeRange: IndexRange = this.getTimestampRange(timestampRange)
    // Because slice is non-inclusive with the last element, we need to increase the end of the range by one to get an accurate slice
    // This is in addition to the addition in getTimestampRange, but both are needed
    const realTimeRange: IndexRange = [timeRange[0], timeRange[1] + 1]
    return this.slice(realTimeRange, 'huh')
  }

  /** returns spliced historyStore */
  @enumerable(false)
  splice(startIdx: number, deleteCount: number, inserts?: StateSnapshot<any>[]) {
    const firstSlice = this.slice([0, startIdx])
    const secondStateSnapshots = this.slice([startIdx + deleteCount, this.getLength()]).getStateSnapshotArray()
    if (inserts) {
      inserts.forEach((snapshop) => firstSlice.push(snapshop))
    }
    secondStateSnapshots.forEach((snapshop) => firstSlice.push(snapshop))
    return firstSlice
  }

  // we can't seem to mutate in place here so for now have to return this
  @enumerable(false)
  insertSnapshot(snapshot: StateSnapshot<any>) {
    const lastLessIndex = this.getIndexLastLessThanTimestamp(snapshot.timestamp)
    debug && console.log(`HistoryStore insertSnapshot, lastLessIndex ${lastLessIndex}, for timestam ${new Date(snapshot.timestamp).toString()}`)
    if (lastLessIndex >= -1 && lastLessIndex < this.getLength()) {
      // new index zero if below range, max index plus one if out of range or after lastlessIndex, if above range index is length so proxy for push
      const newIndex = lastLessIndex < 0 ? 0 : lastLessIndex === this.getLastIndex() ? this.getLength() : lastLessIndex
      debug && console.log('Next lowest ', { lastLessIndex, newIndex, currentMax: this.getLastIndex(), state: snapshot.state })
      this.spliceOneUnprotected(newIndex, snapshot)
    } else {
      debug && console.log(`HistoryStore insertSnapshot, not inserting, lastLessIndex ${lastLessIndex}, store length ${this.getLength()}`)
    }
    return this
  }

  @enumerable(false)
  private spliceOneUnprotected(start: number, snapshot: StateSnapshot<any>) {
    const snapShotKeys: string[] = Object.keys(snapshot.state)
    this.index.push(this.getLength())
    this.timestamp.splice(start, 0, snapshot.timestamp)
    const unsetKeys = this.keys().filter((k) => !snapShotKeys.includes(k))
    unsetKeys.forEach((k: string) => this[k].splice(start, 0, null))

    Object.entries(snapshot.state).forEach(([k, v]) => {
      this.spliceKV(k, v, start)
    })
  }

  @enumerable(false)
  private spliceKV(k: string, v: any, start: number) {
    if (!this[k]) {
      this.backFill(k)
      debug && console.log('Added', k)
      debug && console.log('This is', this)
    }
    this[k].splice(start, 0, v)
    //debug && console.log('This k', this[k])
  }

  @enumerable(false)
  protected pushKV(key: string, value: any) {
    if (!this.hasKey(key)) {
      this.backFill(key)
    }

    this[key].push(value ?? null)
    return { [key]: value }
  }

  @enumerable(false)
  private unshiftKV(key: string, value: any) {
    if (!this.hasKey(key)) {
      this.backFill(key)
    }
    this[key].unshift(value ?? null)
    return { [key]: value }
  }

  /** if key doesnt exist, adds array for key - (NOTE) with one less cell than current length - which is then pushed, shifted or spliced into */
  @enumerable(false)
  protected backFill(key: string) {
    if (!this.hasKey(key)) {
      this[key] = new Array(this.getLength() - 1).fill(null)
    }
  }

  @enumerable(false)
  addKey(key: string) {
    if (!this.isProtectedKey(key)) {
      this[key] = new Array(this.getLength()).fill(null)
    }
  }

  @enumerable(false)
  removeKey(key: string) {
    if (!this.isProtectedKey(key)) {
      delete this[key]
    }
  }

  @enumerable(false)
  lastTimestamp() {
    const lastIndex = this.getLastIndex()
    return this.timestamp[lastIndex]
  }

  static fromStateSnapshotArray(snapshorArray: StateSnapshot<any>[]) {
    const historyStore = new HistoryStore()
    do {
      const snapshot = snapshorArray.shift()
      if (snapshot) historyStore.push(snapshot)
    } while (snapshorArray.length > 0)
    return historyStore
  }

  @enumerable(false)
  getIndexLastLessThanTimestampNaiive(t: number) {
    const i = this.timestamp.findIndex((ti) => ti > t)
    return Math.max(-1, i - 1)
  }

  /** Gets the index (or bucket timestamp) of the most recent non-null value for each reading before provided timestamp
   * @param timestamp - The timestamp to get the most-recent-reading too
   * @param getIndexesOrTimestamps - If indexes, returns the index of the most recent non-null value. If timestamps, map is instead readinKey: timestamp. Useful when using with pushKeywise
   * @returns An object of key -> index (or timestamp) of most recent non-null key value before timestamp
   */
  getIndexLastNonNullKeyLessThanTimestamp(timestamp: number, getIndexesOrTimestamps: 'indexes' | 'timestamps' = 'indexes') {
    const latestTsIdx = this.getIndexLastLessThanTimestamp(timestamp)

    let latestNonNullTimestamps: { [key: string]: number | null } = {}

    this.keys().forEach((key) => {
      latestNonNullTimestamps[key] = null
      /** Traverse timestamps backwards, lookng for non-null value of reading */
      if (latestTsIdx === -1) return
      for (let idx = latestTsIdx; idx >= 0; idx--) {
        if (this[key][idx] != null) {
          latestNonNullTimestamps[key] = idx
          break
        }
      }
    })

    /** Transform idx -> timestamp if desired */
    if (getIndexesOrTimestamps == 'timestamps') {
      Object.keys(latestNonNullTimestamps).forEach((readingKey) => {
        if (latestNonNullTimestamps[readingKey] != null) latestNonNullTimestamps[readingKey] = this.timestamp[latestNonNullTimestamps[readingKey]]
      })
    }

    return latestNonNullTimestamps
  }

  /** Runs a binary search through timestamps
   * Gets latest index less than or equal to timestamp
   * */
  @enumerable(false)
  /** Gets the index of the latest time less than provided timestamp
   * @param t - Timestamp to find less than
   * @returns The latest index before the provided timestamp or -1 if there is no such timestamp (the provided timestamp is before the earliest index)
   */
  getIndexLastLessThanTimestamp(t: number): number {
    if (this.timestamp.length === 0) return -1

    let i

    const testSegment = (first: number, last: number, timestamp: number): number => {
      const middle: number = Math.floor((last + first) / 2)
      if (Math.abs(last - first) <= 1) {
        return this.timestamp[first] <= timestamp ? first : last
      } else if (this.timestamp[middle] > timestamp) {
        return testSegment(first, middle, timestamp)
      } else {
        return testSegment(middle, last, timestamp)
      }
    }

    const lessThanRange = t <= this.timestamp[0]
    const moreThanRange = t > this.timestamp[this.timestamp.length - 1]
    if (moreThanRange) {
      debug && console.log('More than range ', { t, last: this.timestamp[this.timestamp.length - 1] })
      i = this.timestamp.length - 1
    } else if (lessThanRange) {
      debug && console.log('less than (or equal to) range ', { t, first: this.timestamp[0], diff: this.timestamp[0] - t })
      //i = -1;
      return -1
    } else {
      i = testSegment(0, this.getLastIndex(), t)
    }

    let res = Math.max(0, Math.min(i, this.getLastIndex()))

    debug && console.log('Timestamp', this.timestamp)
    debug && console.log(`Latest timestamp before ${getNiceDate(t)} (${t}) is ${getNiceDate(this.timestamp[res])} (${this.timestamp[res]}) [${res}]`)
    return Math.max(0, Math.min(i, this.getLastIndex()))
  }

  @enumerable(false)
  getStateSnapshotArray(sort?: SortOrder): StateSnapshot<any>[] {
    const clone = this.slice([0, this.getLength()])
    const snapshotArray: StateSnapshot<any>[] = []
    do {
      const snapShot = clone.shift()
      if (snapShot) snapshotArray.push(snapShot)
    } while (clone.getLength() > 0)
    if (sort) {
      const sortFn = (sn1: StateSnapshot<any>, sn2: StateSnapshot<any>) => {
        return sort * (sn1.timestamp - sn2.timestamp)
      }
      snapshotArray.sort(sortFn)
    }
    return snapshotArray
  }

  @enumerable(false)
  sort(order: SortOrder) {
    if (this.getLength() > 1000) debug && console.log('Warning: Performance will be sub optimal on massive History Stores')
    const res = this.getStateSnapshotArray(order)
    debug && console.log('Snampshot res', res)
    Object.assign(this, HistoryStore.fromStateSnapshotArray(res))
  }

  @enumerable(false)
  isTimeOrdered(sort: SortOrder) {
    const limit = this.getLastIndex()
    const isSorted = this.timestamp.every((t, i) => (i < limit ? sort * (t - this.timestamp[i + 1]) <= 0 : true))
    if (isSorted === false) {
      debug &&
        console.log(
          'out of order at ',
          this.timestamp.findIndex((t, i) => sort * (t - this.timestamp[i + 1]) > 0),
        )
      debug && console.log('lastIndex was', limit)
    }
    return isSorted
  }

  @enumerable(false)
  getArrayLength = () => Object.fromEntries(['timestamp', 'index', ...this.keys()].map((k) => [k, this[k].length]))

  @enumerable(false)
  hasEqualLengthArrays(includeIndex: boolean = false) {
    const lArr: number[] = this.keys().map((k) => this[k].length)
    if (includeIndex) {
      /** index in deprecation */
      lArr.push(this.index.length)
    }

    lArr.push(this.timestamp.length)
    return lArr.every((l) => l === this.getLength())
  }

  @enumerable(false)
  validate(sortOrder: SortOrder = SortOrder.ASCENDING) {
    return this.hasEqualLengthArrays() && this.isTimeOrdered(sortOrder)
  }

  @enumerable(false)
  validateBounds({ start, end }: { start: number; end: number }) {
    const startAfter = this.getFirstTimestamp() > start
    const endBefore = this.getLastTimestamp() < end
    return startAfter && endBefore
  }

  @enumerable(false)
  getStateSnapshotByIndex(idx: number): StateSnapshot<any> | undefined {
    let stateSnapshot: StateSnapshot<any> | undefined = undefined
    if (idx > -1 && idx < this.getLength()) {
      stateSnapshot = {
        timestamp: this.timestamp[idx],
        state: {},
      }
      for (let k of this.keys()) {
        stateSnapshot.state[k] = this[k][idx]
      }
    }
    return stateSnapshot
  }

  @enumerable(false)
  generateStatistics() {
    const hasStatistics = Object.keys(this).filter((key) => key === 'statistics')
    if (hasStatistics.length === 0) {
      this.statistics = new CMVarAggregationMap()
    }
    const indxArr = this.timestamp
    const stateSnapshotArr = indxArr.map((_t, ind) => this.getStateSnapshotByIndex(ind))

    stateSnapshotArr.map((stateSnapshot) => stateSnapshot && this.updateStatistics(stateSnapshot))
    return this.getStatistics()
  }

  @enumerable(false)
  getLastNullValueIndex(k: string, beforeIdx?: number): number | null {
    let idx: number | null = null
    if (this.keys().includes(k)) {
      const arr = this[k] as NumNullArr
      for (let i = (beforeIdx ?? arr.length) - 1; i >= 0; i--) {
        if (arr[i] === null) {
          idx = i
          break
        }
      }
    }
    return idx
  }

  @enumerable(false)
  getNextNonNullValueIndex(k: string, index: number) {
    let idx: number | null = null
    if (this.keys().includes(k)) {
      const arr = this[k] as NumNullArr
      for (let i = index; i < arr.length; i++) {
        if (arr[i] !== null) {
          idx = i
          break
        }
      }
    }
    return idx
  }

  @enumerable(false)
  getPreviousNonNullValueIndex(k: string, index: number) {
    let idx: number | null = null
    if (this.keys().includes(k)) {
      const arr = this[k] as NumNullArr
      for (let i = index; i >= 0; i--) {
        if (arr[i] !== null) {
          idx = i
          break
        }
      }
    }
    return idx
  }

  @enumerable(false)
  inferMissingValues(startVal: number, endVal: number, startEndInclusiveTimestamps: number[]) {
    const startTimestamp = startEndInclusiveTimestamps.shift()
    const endTimestamp = startEndInclusiveTimestamps.pop()

    let res: Record<number, number> | null = null

    if (isNumber(startVal) && isNumber(endVal) && isNumber(startTimestamp) && isNumber(endTimestamp) && startTimestamp < endTimestamp) {
      res = Object.fromEntries(
        startEndInclusiveTimestamps.map((ts) => {
          const diff = endVal - startVal
          const periodTotal = endTimestamp - startTimestamp
          const period = ts - startTimestamp
          const inferredVal = (period / periodTotal) * diff + startVal
          return [ts, inferredVal]
        }),
      )
      debug && console.log('missingVals', res)
      return res
    } else {
      debug && console.log('failed predicate', { startTimestamp, endTimestamp, startVal, endVal })
    }
  }

  @enumerable(false)
  insertMissingValues(k: string) {
    if (this.keys().includes(k)) {
      let lastNullIndex: null | number = this.getLastNullValueIndex(k)
      let tempLimit = 0
      while (lastNullIndex && tempLimit < 1000) {
        const previousNonNullIdx = this.getPreviousNonNullValueIndex(k, lastNullIndex)
        const nextNonNullIdx = this.getNextNonNullValueIndex(k, lastNullIndex)

        debug && console.log({ previousNonNullIdx, lastNullIndex, nextNonNullIdx })
        if (isNumber(nextNonNullIdx)) {
          if (isNumber(previousNonNullIdx)) {
            const startVal = this[k][previousNonNullIdx]
            const endVal = this[k][nextNonNullIdx]
            const missingValues = this.inferMissingValues(startVal, endVal, this.timestamp.slice(previousNonNullIdx, nextNonNullIdx + 1))
            debug && console.log({ lastNullIndex, missingValues })
            if (missingValues) {
              Object.entries(missingValues).forEach(([ts, val], i) => {
                const insertIdx = previousNonNullIdx + 1 + i
                const correspondingTs = this.timestamp[insertIdx]
                if (correspondingTs !== parseInt(ts)) {
                  debug && console.log('WARN: mismatch timestamps', { correspondingTs, ts, val })
                } else {
                  this[k][insertIdx] = val
                }
              })
            }
          }
        }

        lastNullIndex = this.getLastNullValueIndex(k, lastNullIndex)
        tempLimit++
      }
    }
  }

  /** returns the total span in milliseconds */
  @enumerable(false)
  getTimespan() {
    const firstTimestamp = this.getFirstTimestamp()
    const lastTimeStamp = this.getLastTimestamp()
    return (lastTimeStamp ?? 0) - (firstTimestamp ?? 0)
  }
}
